import { useEffect, useState, useCallback } from 'react'
import { supabase } from '@/integrations/supabase/client'

interface Conversation {
  id: string
  participant_1_id: string
  participant_2_id: string
  last_message_at?: string
  last_message_preview?: string
  participant_1: {
    id: string
    display_name: string | null
    full_name: string | null
    avatar_url: string | null
  }
  participant_2: {
    id: string
    display_name: string | null
    full_name: string | null
    avatar_url: string | null
  }
}

export function useConversations() {
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const loadConversations = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      // Step 1: Fetch conversations without user_profiles join (avoids cross-schema FK limitation)
      const { data: convos, error } = await supabase
        .from('conversations')
        .select('*')
        .or(`participant_1_id.eq.${user.id},participant_2_id.eq.${user.id}`)
        .order('last_message_at', { ascending: false, nullsFirst: false })

      if (error) throw error
      if (!convos || convos.length === 0) {
        setConversations([])
        return
      }

      // Step 2: Extract unique participant IDs
      const participantIds = [...new Set(
        convos.flatMap(c => [c.participant_1_id, c.participant_2_id])
      )]

      // Step 3: Fetch user_profiles separately
      const { data: profiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select('id, display_name, full_name, avatar_url')
        .in('id', participantIds)

      if (profilesError) throw profilesError

      // Step 4: Merge data using profileMap
      const profileMap = Object.fromEntries(
        (profiles || []).map(p => [p.id, p])
      )

      const enrichedConvos = convos.map(c => ({
        ...c,
        participant_1: profileMap[c.participant_1_id] || {
          id: c.participant_1_id,
          display_name: null,
          full_name: null,
          avatar_url: null
        },
        participant_2: profileMap[c.participant_2_id] || {
          id: c.participant_2_id,
          display_name: null,
          full_name: null,
          avatar_url: null
        }
      }))

      setConversations(enrichedConvos)
    } catch (err) {
      setError(err as Error)
    } finally {
      setLoading(false)
    }
  }, [])

  const startConversation = useCallback(async (otherUserId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('start-conversation', {
        body: { otherUserId }
      })

      if (error) throw error
      
      // Refresh conversations list
      await loadConversations()
      
      return data.conversation
    } catch (err) {
      setError(err as Error)
      throw err
    }
  }, [loadConversations])

  useEffect(() => {
    loadConversations()
  }, [loadConversations])

  return {
    conversations,
    loading,
    error,
    startConversation,
    refresh: loadConversations
  }
}
